import { useCallback, useEffect, useState } from 'react';
import { AlertCode } from 'shared/types/checkout';
import { DiscountProduct, ProcessProductResponse } from 'shared/types/product';
import { DEFAULT_PAGE_LIMIT } from 'helpers/constants';
import { sdk } from 'sdk';
import { getAccountId, useAppSelector } from 'store';
import { makeSDKCallWithRetries } from 'utils';
import { useEnrollments } from '../useEnrollments';

const INITIAL_DATA = {
  data: {
    total: 0,
    products: [] as DiscountProduct[],
    isError: false,
    error: '' as AlertCode,
  },
  isError: false,
  isLoading: true,
};

export const useDiscountedProducts = (discountCode: string) => {
  const customerId = useAppSelector(getAccountId);

  const [products, setProducts] = useState(INITIAL_DATA);

  const [currentPage, setCurrentPage] = useState(1);
  const offset = (currentPage - 1) * DEFAULT_PAGE_LIMIT;
  const { enrollments, isLoading: isEnrollmentsLoading } = useEnrollments();

  const fetchProducts = useCallback(async () => {
    if (!discountCode) {
      setProducts((prev) => ({
        data: {
          ...prev.data,
          error: AlertCode.DISCOUNT_CODE_NOT_PROVIDED,
        },
        isError: true,
        isLoading: false,
      }));
      return;
    }

    const response = await makeSDKCallWithRetries(
      'product/getDiscountedProducts',
      customerId,
      () =>
        sdk.callAction<ProcessProductResponse>({
          actionName: 'product/getDiscountedProducts',
          payload: {
            discountCode,
            userEnrollments: enrollments,
            offset,
            limit: DEFAULT_PAGE_LIMIT,
          },
        }),
    );

    const responseData = (!response.isError &&
      response.data) as ProcessProductResponse;

    const error = response.isError
      ? response.error
      : responseData?.isError
        ? responseData.error
        : null;

    if (error) {
      let errorCode = (error.code as string) ?? '';
      const errorMessage =
        error?.message ?? (error ? JSON.stringify(error) : 'unknown');

      console.error(
        `Error in getDiscountedProducts extension for customer: ${customerId} with error code: ${errorCode} and error message: ${errorMessage}`,
      );

      if (!Object.values(AlertCode).includes(errorCode as AlertCode)) {
        errorCode = AlertCode.FALLBACK_WITH_RELOAD_INFORMATION;
      }

      setProducts({
        data: {
          ...INITIAL_DATA.data,
          error: errorCode as AlertCode,
        },
        isError: true,
        isLoading: false,
      });
      return;
    }

    if (!responseData.isError) {
      const newProducts = responseData.products ?? [];
      const totalCount = responseData.total ?? 0;

      setProducts({
        data: {
          products: [...products.data.products, ...newProducts],
          total: totalCount,
          isError: false,
          error: '' as AlertCode,
        },
        isError: false,
        isLoading: false,
      });

      if (offset + DEFAULT_PAGE_LIMIT < totalCount) {
        setCurrentPage((prev) => prev + 1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, discountCode, offset, enrollments, isEnrollmentsLoading]);

  useEffect(() => {
    if (!isEnrollmentsLoading) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, discountCode, isEnrollmentsLoading]);

  return products;
};
